import React from 'react';
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboardIcon, FolderKanbanIcon, FileStackIcon, MessagesSquareIcon, ReceiptIcon, UsersIcon, ScrollTextIcon, UserIcon, LogOutIcon, MenuIcon, XIcon } from 'lucide-react';
import { Logo } from '../../components/brand/Logo';
import { Seo } from '../../components/Seo';
import { OrgSwitcher } from '../../components/portal/OrgSwitcher';
import { NotificationBell } from '../../components/portal/NotificationBell';
import { Avatar } from '../../components/portal/Avatar';
import { usePortalAuth } from '../../context/PortalAuth';
import { PortalOrgProvider, usePortalOrg } from '../../context/PortalOrg';
import { can, type PortalAction } from '../../lib/portalPermissions';
import { displayName } from '../../utils/portalFormat';
export function PortalLayout() {
  const {
    user,
    loading
  } = usePortalAuth();
  const location = useLocation();
  if (loading) {
    return <div className="min-h-screen w-full bg-charcoal" aria-busy="true" data-id="element-3006" data-mp="kqpq6r05oz6e" />;
  }
  if (!user) return <Navigate to="/portal/login" replace state={{
    from: location.pathname
  }} data-id="element-3007" data-mp="g09ebbbzhlm5" />;
  if (!user.firstName) return <Navigate to="/portal/welcome" replace data-id="element-3008" data-mp="b0wr2ph7acj1" />;
  return <PortalOrgProvider data-id="element-3009" data-mp="b1prl8pkg5sq">
      <Shell data-id="element-3010" data-mp="aytpd86oquyh" />
    </PortalOrgProvider>;
}
interface NavItem {
  label: string;
  icon: typeof LayoutDashboardIcon;
  to: (orgId: string) => string;
  requires?: PortalAction[];
}
const ORG_NAV: NavItem[] = [{
  label: 'Projects',
  icon: FolderKanbanIcon,
  to: o => `/portal/orgs/${o}/projects`
}, {
  label: 'Deliverables',
  icon: FileStackIcon,
  to: o => `/portal/orgs/${o}/deliverables`
}, {
  label: 'Messages',
  icon: MessagesSquareIcon,
  to: o => `/portal/orgs/${o}/messages`,
  requires: ['messages.view']
}, {
  label: 'Invoices',
  icon: ReceiptIcon,
  to: o => `/portal/orgs/${o}/invoices`,
  requires: ['invoices.viewAll', 'invoices.viewOwn']
}, {
  label: 'Team',
  icon: UsersIcon,
  to: o => `/portal/orgs/${o}/team`,
  requires: ['members.list']
}, {
  label: 'Audit log',
  icon: ScrollTextIcon,
  to: o => `/portal/orgs/${o}/audit`,
  requires: ['audit.view']
}];
function Shell() {
  const {
    user,
    signOut
  } = usePortalAuth();
  const {
    orgId,
    role
  } = usePortalOrg();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawer, setDrawer] = React.useState(false);
  React.useEffect(() => setDrawer(false), [location.pathname]);
  if (!user) return null;
  const onSignOut = async () => {
    await signOut();
    navigate('/portal/login');
  };
  const visible = ORG_NAV.filter(item => !item.requires || item.requires.some(action => can(role, action)));
  const linkClass = ({
    isActive
  }: {
    isActive: boolean;
  }) => `flex items-center gap-3 rounded-sm px-3 py-2.5 text-[15px] transition-colors duration-150 ${isActive ? 'bg-white/12 text-white' : 'text-white/65 hover:bg-white/8 hover:text-white'}`;
  const sidebar = <div className="flex h-full flex-col" data-id="element-3011" data-mp="2e96tgobuung8">
      <Link to="/" aria-label="Cavalla, back to the website" className="inline-block" data-id="element-3012" data-mp="1vlej6eeex4fh">
        <Logo variant="reversed" data-id="element-3013" data-mp="1vn925n64imgx" />
      </Link>

      <nav aria-label="Portal" className="mt-9 flex-1 space-y-7 overflow-y-auto" data-id="element-3014" data-mp="1qncwzklowozs">
        <NavLink to="/portal/dashboard" className={linkClass} data-id="element-3015" data-mp="24mm5dy3nmet7">
          <LayoutDashboardIcon className="h-4 w-4" aria-hidden="true" data-id="element-3016" data-mp="24jq5434c3e1l" />
          All engagements
        </NavLink>

        {orgId && <div data-id="element-3017" data-mp="2ejh0e70pxx2q">
            <p className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/45" data-id="element-3018" data-mp="2eiz6vi5fxwyi">Organisation</p>
            <OrgSwitcher data-id="element-3019" data-mp="29kxcw26h8d89" />
            <ul className="mt-3 space-y-0.5" data-id="element-3020" data-mp="2nckln1hlve7x">
              {visible.map(item => {
            const Icon = item.icon;
            return <li key={item.label} data-id="element-3021" data-mp="2jca2en4syrb4">
                    <NavLink to={item.to(orgId)} className={linkClass} data-id="element-3022" data-mp="2jdcw12tif7uq">
                      <Icon className="h-4 w-4" aria-hidden="true" data-id="element-3023" data-mp="2xb1fq7buc8y7" />
                      {item.label}
                    </NavLink>
                  </li>;
          })}
            </ul>
          </div>}
      </nav>

      <div className="mt-6 border-t border-white/12 pt-5" data-id="element-3024" data-mp="3gf4xdnlyoxvl">
        <NavLink to="/portal/account" className={linkClass} data-id="element-3025" data-mp="3gdk2txzndexq">
          <UserIcon className="h-4 w-4" aria-hidden="true" data-id="element-3026" data-mp="3l5tov2xn5uuq" />
          Account
        </NavLink>
        <button type="button" onClick={() => void onSignOut()} className="mt-0.5 flex w-full items-center gap-3 rounded-sm px-3 py-2.5 text-[15px] text-white/65 transition-colors duration-150 hover:bg-white/8 hover:text-white" data-id="element-3027" data-mp="3q5g056twt10w">
          <LogOutIcon className="h-4 w-4" aria-hidden="true" data-id="element-3028" data-mp="32flpmvx2bojt" />
          Sign out
        </button>
      </div>
    </div>;
  return <>
      <Seo title="Client portal" description="The Cavalla client portal." path="/portal" noIndex data-id="element-3029" data-mp="37fq5qku26h9i" />
      <div className="flex min-h-screen w-full bg-charcoal" data-id="element-3030" data-mp="hobikzxiovwf">
        <aside className="surface-dark sticky top-0 hidden h-screen w-72 shrink-0 px-5 py-7 lg:block" data-id="element-3031" data-mp="mohg2wqgxcmf">{sidebar}</aside>

        {drawer && <div className="fixed inset-0 z-40 lg:hidden" data-id="element-3032" data-mp="275716pqfx1li">
            <button type="button" className="absolute inset-0 bg-[#020B18]/70" aria-label="Close menu" onClick={() => setDrawer(false)} data-id="element-3033" data-mp="227p0tnqhnup4" />
            <aside className="surface-dark relative h-full w-[min(300px,85vw)] px-5 py-7" data-id="element-3034" data-mp="1xfe1on3kocxm">
              <button type="button" onClick={() => setDrawer(false)} className="absolute right-3 top-5 inline-flex h-10 w-10 items-center justify-center text-white/70" aria-label="Close menu" data-id="element-3035" data-mp="1sgknstkdijwx">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-3036" data-mp="1ehkw1d1kdowj" />
              </button>
              {sidebar}
            </aside>
          </div>}

        <div className="flex min-w-0 flex-1 flex-col" data-id="element-3037" data-mp="19kksb8s6sy8l">
          <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-line bg-charcoal/95 px-4 backdrop-blur sm:px-8" data-id="element-3038" data-mp="38wdox4b2c1ks">
            <button type="button" onClick={() => setDrawer(true)} className="inline-flex h-10 w-10 items-center justify-center rounded-sm text-body hover:bg-panel lg:hidden" aria-label="Open menu" data-id="element-3039" data-mp="33xkclf7iigm7">
              <MenuIcon className="h-5 w-5" aria-hidden="true" data-id="element-3040" data-mp="sagv46qsdrvz" />
            </button>
            <div className="flex-1" data-id="element-3041" data-mp="x7z6s4w6ul7o" />
            <NotificationBell data-id="element-3042" data-mp="x91ts1qe43ib" />
            <Link to="/portal/account" className="flex items-center gap-3 rounded-sm px-2 py-1.5 transition-colors duration-150 hover:bg-panel" data-id="element-3043" data-mp="x661asxba719">
              <Avatar user={user} size="sm" data-id="element-3044" data-mp="1pw4souyzag93" />
              <span className="hidden text-[14px] text-ink sm:block" data-id="element-3045" data-mp="1putpoxdcxcj1">{displayName(user)}</span>
            </Link>
          </header>

          <main id="main" className="flex-1 px-4 py-8 sm:px-8 lg:px-12 lg:py-10" data-id="element-3046" data-mp="1fygu1ty94oln">
            <div className="mx-auto w-full max-w-6xl" data-id="element-3047" data-mp="ihrqzbbfmtb">
              <Outlet data-id="element-3048" data-mp="golvm8j16md" />
            </div>
          </main>
        </div>
      </div>
    </>;
}